import * as React from 'react';
import { FormattedNumber } from 'react-intl';
import { getCurrencyConversion } from '@/redux/modules/currency';
import { preferredCurrencySelector } from '@/redux/modules/bidderPreference';
import styled from 'styled-components';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { useSelector } from 'react-redux';

type Props = {
    /** For passing in custom styles */
    className?: any;
    /** For passing in color white to the text component */
    colorWhite?: boolean;
    /** For passing in custom styles to the currency display */
    currencyClassName?: any;
    /** Renders the text as h3 when true */
    h3?: boolean;
    /** when true, does not render the grey background */
    noBackground?: boolean;
    /** when true, does not render any cent digits */
    noFraction?: boolean;
    /** The denomination of the source currency e.g. 'USD' or 'GBP' */
    sourceCurrency: string;
    /** The source currency amount, this value will be converted to the user's preferred currency */
    sourceValue: number;
    /** When true, will render () around the converted currency. This is used for the message scroller's unique display */
    wrapWithParens?: boolean;
};

/**
 * Displays the user preferred currency, converted from the input currency
 */
const FormattedPreferredCurrency = ({
    className,
    colorWhite = false,
    currencyClassName,
    h3 = false,
    noBackground = false,
    noFraction = false,
    sourceCurrency,
    sourceValue,
    wrapWithParens = false,
}: Props) => {
    const preferredCurrency = useSelector(preferredCurrencySelector);
    const targetCurrency = useSelector((state) => getCurrencyConversion(state, preferredCurrency));

    const sourceCurrencyConversion = useSelector((state) => getCurrencyConversion(state, sourceCurrency));

    // Show nothing if the source/target currencies are the same or don't exist in the currency conversion table
    if (
        !Boolean(targetCurrency) ||
        !Boolean(sourceCurrencyConversion) ||
        sourceCurrency === targetCurrency.currencyCode
    ) {
        return null;
    }

    // Convert source currency to USD, we do this because we only store conversions from usd, so we always need this.
    const sourceInUSD = sourceValue / sourceCurrencyConversion.conversionToUsd;

    // Convert from USD to target currency
    const valueInTargetCurrency = sourceInUSD * targetCurrency.conversionToUsd;

    const fractionDigits = noFraction ? 0 : targetCurrency.fractionSize;
    return (
        <PreferredCurrencyContainer className={className}>
            {wrapWithParens && '('}
            <ConvertedCurrency $noBackground={noBackground} className={currencyClassName}>
                <Text colorWhite={colorWhite} ellipsis h3={h3} secondary={!h3}>
                    <FormattedNumber
                        currency={targetCurrency.currencyCode}
                        maximumFractionDigits={fractionDigits}
                        minimumFractionDigits={fractionDigits}
                        style="currency"
                        value={valueInTargetCurrency}
                    />
                </Text>
            </ConvertedCurrency>
            {wrapWithParens && ')'}
        </PreferredCurrencyContainer>
    );
};

export default React.memo<Props>(FormattedPreferredCurrency);

const PreferredCurrencyContainer = styled.div`
    display: flex;
    overflow: hidden;
`;

const ConvertedCurrency = styled.div<{ $noBackground: boolean }>`
    padding: 0 5px;
    background-color: ${(props) => !props.$noBackground && props.theme.colors.grey400};
`;
